import { logger } from '../../../utils/logger'
import {
  fetchProgramDesignations,
  toSearchSelectOptions,
} from '../../Block/donation/useFetchProgramDesignations'
import type { ServiceHandler } from './service-registry'
import type {
  GetProgramDesignationsRequest,
  GetProgramDesignationsResponse,
} from '@custom-embedded-code-sdk/methods/get-program-designations'

/**
 * GET_PROGRAM_DESIGNATIONS service handler
 *
 * Handles requests to retrieve program designations for the current campaign.
 * Supports pagination via the page parameter.
 */
export const getProgramDesignations: ServiceHandler<
  GetProgramDesignationsRequest,
  GetProgramDesignationsResponse
> = async ({
  iframeId,
  messageId,
  payload,
  sendSuccessResponse,
  sendErrorResponse,
  pageConfig,
}) => {
  const campaignId = pageConfig.campaignId

  try {
    if (!campaignId) {
      throw new Error('CustomEmbeddedCode:getProgramDesignations: Campaign ID not available')
    }

    // TODO: CL-51093 improve payload validation with Yup/Zod library
    if (!payload || typeof payload !== 'object') {
      sendErrorResponse(iframeId, messageId, 'Payload is expected to be an object')
      return
    }

    if (
      payload.page !== undefined &&
      (typeof payload.page !== 'number' || !Number.isFinite(payload.page) || payload.page < 1)
    ) {
      sendErrorResponse(iframeId, messageId, 'Invalid page')
      return
    }
    const page = typeof payload.page === 'number' ? payload.page : 1

    const { designations: rawDesignations, pagination } = await fetchProgramDesignations({
      campaignId,
      searchString: '',
      page,
    })

    const designations = toSearchSelectOptions(rawDesignations)

    logger(
      'info',
      'CustomEmbeddedCode:getProgramDesignations: Program designations fetched successfully',
      {
        context: { iframeId, messageId, campaignId, page, orgId: pageConfig.orgId },
      },
    )

    sendSuccessResponse(iframeId, messageId, {
      designations,
      pagination,
    })
  } catch (error) {
    logger(
      'error',
      new Error('CustomEmbeddedCode:getProgramDesignations: Failed to fetch program designations', {
        cause: error,
      }),
      {
        context: { iframeId, messageId, campaignId, orgId: pageConfig.orgId },
      },
    )
    sendErrorResponse(iframeId, messageId, 'Unable to fetch program designations')
  }
}
